import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

// Log reminders via Web Push (Phase N) — self-generated VAPID, no vendor. The server
// pushes at the user's chosen times (skipping ones they've already logged near); this
// screen handles device permission/subscription + the reminder schedule.

const pushSupported = typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window
  && (window.isSecureContext ?? false);

// Convert the URL-safe base64 VAPID public key to the Uint8Array pushManager wants.
const urlB64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const localTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch (e) { return 'UTC'; }
};

const ReminderSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [times, setTimes] = useState(['09:00', '20:00']);
  const [skipIfLogged, setSkipIfLogged] = useState(true);
  const [vapidKey, setVapidKey] = useState(null);
  const [permission, setPermission] = useState(pushSupported ? Notification.permission : 'unsupported');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [prefRes, keyRes] = await Promise.all([
          apiClient.get('/api/notifications/preferences'),
          apiClient.get('/api/notifications/vapid-public-key'),
        ]);
        if (cancelled) return;
        const p = prefRes.data;
        setEnabled(p.enabled);
        if (Array.isArray(p.times) && p.times.length) setTimes(p.times);
        setSkipIfLogged(p.skip_if_logged);
        setVapidKey(keyRes.data.public_key || null);
        if (pushSupported) {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          if (!cancelled) setSubscribed(!!sub);
        }
      } catch (e) {
        // Non-fatal — the section just shows defaults.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const ensureSubscribed = useCallback(async () => {
    if (!pushSupported) { toast.error('Reminders need the installed app on your phone.'); return false; }
    if (!vapidKey) { toast.error('Reminders aren’t configured on the server yet.'); return false; }
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== 'granted') {
      toast.error('Notifications are blocked. Turn them on in your browser settings.');
      return false;
    }
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapidKey),
      });
    }
    const json = sub.toJSON();
    await apiClient.post('/api/notifications/subscribe', {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      user_agent: (navigator.userAgent || '').slice(0, 400),
    });
    setSubscribed(true);
    return true;
  }, [vapidKey]);

  const save = async () => {
    const clean = [...new Set(times.filter((t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t)))].sort();
    if (enabled && clean.length === 0) { toast.error('Add at least one reminder time.'); return; }
    setSaving(true);
    try {
      // Turning reminders on requires a live device subscription first.
      if (enabled && !subscribed) {
        const ok = await ensureSubscribed();
        if (!ok) { setSaving(false); return; }
      }
      await apiClient.put('/api/notifications/preferences', {
        enabled,
        times: clean,
        timezone: localTimezone(),
        skip_if_logged: skipIfLogged,
      });
      setTimes(clean.length ? clean : times);
      toast.success(enabled ? 'Reminders saved.' : 'Reminders turned off.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Couldn’t save reminders.');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    try {
      if (!subscribed) { const ok = await ensureSubscribed(); if (!ok) return; }
      await apiClient.post('/api/notifications/test');
      toast.success('Test sent — check your notifications.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Couldn’t send a test notification.');
    }
  };

  const setTimeAt = (i, v) => setTimes((prev) => prev.map((t, j) => (j === i ? v : t)));
  const removeTimeAt = (i) => setTimes((prev) => prev.filter((_, j) => j !== i));
  const addTime = () => setTimes((prev) => [...prev, '12:00']);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Log reminders</CardTitle>
      </CardHeader>
      <CardContent className="p-6 pt-0 space-y-5">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Get a gentle nudge to log at the times you choose. Once you’ve finished the day’s
          log, it stays quiet — so it only nudges when something’s actually left to log.
        </p>

        {!pushSupported && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
            Reminders work in the installed app on your phone (they need a secure connection).
            Add Vegetablah to your home screen, open it, then set them up here.
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        ) : (
          <>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                disabled={!pushSupported}
              />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Remind me to log
              </span>
            </label>

            {enabled && (
              <>
                <div className="space-y-2">
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Reminder times
                  </span>
                  {times.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={t}
                        onChange={(e) => setTimeAt(i, e.target.value)}
                        className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 text-sm text-gray-900 dark:text-gray-100"
                      />
                      {times.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeTimeAt(i)} title="Remove">
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {times.length < 12 && (
                    <Button type="button" variant="outline" size="sm" onClick={addTime} className="flex items-center">
                      <PlusIcon className="w-4 h-4 mr-1.5" /> Add a time
                    </Button>
                  )}
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={skipIfLogged}
                    onChange={(e) => setSkipIfLogged(e.target.checked)}
                    className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Don’t remind me once today’s log is complete
                  </span>
                </label>
              </>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <Button type="button" onClick={save} disabled={saving || !pushSupported}>
                {saving ? 'Saving…' : 'Save reminders'}
              </Button>
              {pushSupported && (
                <Button type="button" variant="outline" onClick={sendTest} disabled={saving}>
                  Send a test
                </Button>
              )}
            </div>
            {permission === 'denied' && (
              <p className="text-xs text-red-600 dark:text-red-400">
                Notifications are blocked for this site. Re-enable them in your browser settings to use reminders.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReminderSettings;
